import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatError } from "@/lib/api";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Wallet, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

// Basit math CAPTCHA üretici
const genCaptcha = () => {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  return { a, b, answer: a + b };
};

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [captcha, setCaptcha] = useState(genCaptcha);
  const [captchaInput, setCaptchaInput] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Şifremi unuttum akışı
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStage, setResetStage] = useState("request");
  const [resetCode, setResetCode] = useState("");
  const [resetNewPw, setResetNewPw] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  const refreshCaptcha = () => {
    setCaptcha(genCaptcha());
    setCaptchaInput("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (parseInt(captchaInput, 10) !== captcha.answer) {
      setError("Güvenlik doğrulaması hatalı. Lütfen tekrar deneyin.");
      refreshCaptcha();
      return;
    }
    setLoading(true);
    try {
      await login(email, password, remember);
      navigate("/");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = formatError(detail) || err.message;
      setError(msg);
      if (status === 429) {
        // Kilit — CAPTCHA'yı da yenile ki tekrar denemek için uğraşsın
        refreshCaptcha();
      } else {
        refreshCaptcha();
      }
    } finally {
      setLoading(false);
    }
  };

  const startReset = () => {
    setResetEmail(email || "");
    setResetStage("request");
    setResetCode("");
    setResetNewPw("");
    setResetOpen(true);
  };

  const requestCode = async () => {
    if (!resetEmail) { toast.error("E-posta girin"); return; }
    setResetBusy(true);
    try {
      const { data } = await api.post("/auth/password-reset/request", { email: resetEmail });
      toast.success(data.email_sent
        ? "Doğrulama kodu e-postanıza gönderildi"
        : "Kod oluşturuldu. E-posta servisi henüz yapılandırılmadıysa yöneticiyle görüşün.");
      setResetStage("confirm");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Hata"); }
    finally { setResetBusy(false); }
  };

  const confirmReset = async () => {
    if (!resetCode || resetNewPw.length < 6) { toast.error("Kod ve yeni şifre (min 6 karakter) girin"); return; }
    setResetBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", {
        email: resetEmail, code: resetCode, new_password: resetNewPw,
      });
      toast.success("Şifreniz sıfırlandı — yeni şifrenizle giriş yapın");
      setResetOpen(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Hata"); }
    finally { setResetBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative" style={{ background: "hsl(157, 31%, 17%)" }}>
        <img
          src="https://images.unsplash.com/photo-1579487785973-74d2ca7abdd5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="office"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="relative h-full flex flex-col justify-end p-12 text-primary-foreground">
          <div className="flex items-center gap-2 mb-4">
            <Wallet size={28} weight="duotone" />
            <span className="font-heading font-extrabold text-2xl">NAKİT</span>
          </div>
          <h1 className="font-heading font-extrabold text-4xl tracking-tight mb-3">
            Çekler, alacaklar ve nakit — tek panelde.
          </h1>
          <p className="text-primary-foreground/80 max-w-md">
            Şirketlerinizin ödeme trafiğini kontrol altında tutun. Vade takibi, güvenli
            çek yazma tarihleri, aylık nakit akışı ve daha fazlası.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-6 flex items-center gap-2">
            <Wallet size={24} weight="duotone" className="text-primary" />
            <span className="font-heading font-extrabold text-xl">NAKİT</span>
          </div>
          <div className="mb-6 sm:mb-8">
            <h2 className="font-heading font-bold text-2xl">Giriş yap</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Hesabınıza erişerek nakit yönetimine devam edin.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">E-posta</Label>
              <Input id="email" data-testid="login-email" type="email" autoComplete="username"
                value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="password">Şifre</Label>
              <Input id="password" data-testid="login-password" type="password" autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="remember-me" />
                <span>Beni Hatırla</span>
              </label>
              <button type="button" onClick={startReset} data-testid="forgot-password"
                className="text-sm text-primary underline underline-offset-2">
                Şifremi unuttum
              </button>
            </div>

            <div className="rounded-md border border-border bg-secondary/30 p-3">
              <Label htmlFor="captcha" className="text-xs">Güvenlik doğrulaması — robot musun?</Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="text-base font-medium tabular-nums bg-card border border-border rounded-md px-3 py-2 select-none flex-1 text-center">
                  {captcha.a} + {captcha.b} = ?
                </div>
                <Input id="captcha" data-testid="captcha-input" type="number" value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value)} required className="w-20 text-center" />
                <Button type="button" size="icon" variant="ghost" onClick={refreshCaptcha} title="Yenile">
                  <ArrowClockwise size={16} />
                </Button>
              </div>
            </div>

            {error && <div data-testid="login-error" className="text-sm text-destructive">{error}</div>}

            <Button data-testid="login-submit" type="submit" className="w-full" disabled={loading}>
              {loading ? "Giriş yapılıyor…" : "Giriş yap"}
            </Button>
          </form>
        </div>
      </div>

      {/* Şifremi unuttum modal */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{resetStage === "request" ? "Şifremi Unuttum" : "Doğrulama & Yeni Şifre"}</DialogTitle>
          </DialogHeader>
          {resetStage === "request" ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                E-posta adresinizi girin. Kayıtlı ise sıfırlama kodunu yollarız.
              </p>
              <div>
                <Label>E-posta</Label>
                <Input data-testid="reset-email" type="email" value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setResetOpen(false)}>İptal</Button>
                <Button onClick={requestCode} disabled={resetBusy} data-testid="reset-request-btn">
                  {resetBusy ? "Gönderiliyor…" : "Kod Gönder"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                <b>{resetEmail}</b> adresine gönderilen 6 haneli kodu girin ve yeni şifrenizi belirleyin.
              </p>
              <div>
                <Label>Doğrulama Kodu</Label>
                <Input data-testid="reset-code" value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)} placeholder="123456" />
              </div>
              <div>
                <Label>Yeni Şifre (en az 6 karakter)</Label>
                <Input data-testid="reset-new-pw" type="password" value={resetNewPw}
                  onChange={(e) => setResetNewPw(e.target.value)} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setResetStage("request")}>Geri</Button>
                <Button onClick={confirmReset} disabled={resetBusy} data-testid="reset-confirm-btn">
                  {resetBusy ? "Kaydediliyor…" : "Şifreyi Sıfırla"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
